import { motion } from 'framer-motion';
import { AlertTriangle, Loader2, Shield } from 'lucide-react';
import { usePermissionCheck } from '../../hooks/permissionHooks/usePermissionCheck';
import { PERMISSIONS } from '../../config/permissions';

const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, partnerName, isDeleting }) => {
  const { canPerform, isMainVendor } = usePermissionCheck();
  
  // Check if user can delete partners
  const canDeletePartners = canPerform(PERMISSIONS.MODULES.PARTNER_MANAGEMENT, PERMISSIONS.ACTIONS.DELETE_PARTNER) || isMainVendor;

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed top-0 right-0 bottom-0 left-0 z-50 min-h-screen"
    >
      {/* Overlay */}
      <div 
        className="fixed top-0 left-0 right-0 bottom-0 bg-black/30 backdrop-blur-[2px]" 
        onClick={onClose} 
      />
      
      {/* Modal */}
      <div className="flex fixed top-0 right-0 bottom-0 left-0 justify-center items-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="bg-white rounded-3xl w-[400px] mx-auto shadow-xl"
        >
          <div className="px-8 py-6">
            {canDeletePartners ? (
              <div className="flex flex-col items-center">
                <div className="flex justify-center items-center mb-4 w-12 h-12 bg-red-100 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-500" />
                </div>
                <h3 className="text-[22px] font-semibold text-gray-900 mb-2">Delete Partner</h3>
                <p className="mb-1 text-center text-gray-600">
                  Are you sure you want to delete <span className="font-semibold text-gray-900">{partnerName}</span>?
                </p>
                <p className="text-red-500 text-[13px] text-center mb-4">This action cannot be undone.</p>
                
                {/* Warning for data impact */}
                <div className="p-3 mb-4 w-full bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-center text-amber-800">
                    This will also delete all associated branches and their data.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <div className="flex justify-center items-center mb-4 w-12 h-12 bg-red-100 rounded-full">
                  <Shield className="w-6 h-6 text-red-500" />
                </div>
                <h3 className="text-[22px] font-semibold text-gray-900 mb-2">Permission Required</h3>
                <p className="mb-4 text-center text-gray-600">
                  You don't have permission to delete partners. Please contact your administrator.
                </p>
                <div className="p-3 mb-4 w-full bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-center text-blue-800">
                    Required: Delete Partner permission in Partner Management
                  </p>
                </div>
              </div>
            )}
            
            <div className="flex gap-3 justify-end items-center mt-6">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                className="px-5 py-2.5 text-gray-700 font-medium rounded-xl bg-gray-50 hover:bg-gray-100 transition-all duration-200"
              >
                {canDeletePartners ? 'Cancel' : 'Close'}
              </motion.button>
              
              {canDeletePartners && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onConfirm}
                  disabled={isDeleting}
                  className="px-5 py-2.5 bg-red-500 text-white font-medium rounded-xl hover:bg-red-600 transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Partner'
                  )}
                </motion.button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default DeleteConfirmModal;